// Barn settings — where the barn is, what stallSIDE sends on its behalf, and
// what clients are allowed to do for themselves.
//
// Both switches ship off. Turning either on changes how the barn runs: one
// starts mailing every client every week, the other opens the calendar to them.
// Neither is a decision a deploy should make for a barn.
//
// The day matters more than it looks. The digest covers the seven days ahead,
// so Sunday means it lands the evening before the week it describes; picking
// Wednesday would send people a week that is already half over.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

// 0 is a real choice, not a disabled state: some barns want the calendar open
// until the moment the lesson starts.
const CUTOFFS = [
  { value: 0, label: 'At the start time' },
  { value: 2, label: '2 hours before' },
  { value: 12, label: '12 hours before' },
  { value: 24, label: '24 hours before' },
  { value: 48, label: '2 days before' },
  { value: 168, label: 'A week before' },
];

// Lead times a barn is likely to want. Hours, because the sweep runs hourly --
// "2 hours before" is a real option, not a rounded-up day.
const LEADS = [
  { hours: 48, label: '2 days before' },
  { hours: 24, label: 'The day before' },
  { hours: 4, label: '4 hours before' },
  { hours: 2, label: '2 hours before' },
];

const WINDOWS = [
  { value: 24, label: 'Within 24 hours' },
  { value: 48, label: 'Within 2 days' },
  { value: 72, label: 'Within 3 days' },
  { value: 168, label: 'Within a week' },
];

// US zones, because stallSIDE is a US product. The value stored is the IANA
// name; the label is what a person calls it. Arizona and Hawaii are listed
// separately rather than folded into Mountain and Pacific because they don't
// observe DST, which is exactly the case a generic label gets wrong.
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Phoenix', label: 'Arizona (no daylight saving)' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
];

const DAYS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export default function BarnSettingsPage() {
  const { activeRole } = useAuth();
  const canEdit = activeRole === 'owner' || activeRole === 'manager';
  const qc = useQueryClient();
  const [err, setErr] = useState('');

  const settingsQ = useQuery({
    queryKey: ['barn-settings'],
    queryFn: () => api.get('/barn-settings'),
    enabled: canEdit,
    retry: false,
  });

  const save = useMutation({
    mutationFn: (fields) => api.patch('/barn-settings', fields),
    onSuccess: (res) => {
      setErr('');
      // Write the server's copy straight back, so the toggle can't drift from
      // what was actually stored.
      qc.setQueryData(['barn-settings'], { settings: res.settings });
    },
    onError: (e) => setErr(friendly(e)),
  });

  if (!canEdit) {
    return (
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <p className="text-ink-400 text-sm">Only barn owners and managers can change this.</p>
      </div>
    );
  }

  const s = settingsQ.data?.settings;
  const enabled = !!s?.weeklyScheduleEnabled;
  const booking = !!s?.clientBookingEnabled;
  const reminders = !!s?.lessonRemindersEnabled;
  const alerts = !!s?.changeAlertsEnabled;
  const leads = s?.lessonReminderHours ?? [24];

  // At least one lead has to stay selected — the server rejects an empty list,
  // and "reminders on, none scheduled" is a setting that means nothing.
  const toggleLead = (hours) => {
    const next = leads.includes(hours) ? leads.filter((h) => h !== hours) : [...leads, hours];
    if (next.length === 0) return;
    save.mutate({ lessonReminderHours: next.sort((a, b) => b - a) });
  };

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-4 md:space-y-6">
      <header>
        <h1 className="text-2xl md:text-3xl font-semibold">Barn settings</h1>
        <p className="text-ink-400 text-sm mt-1">
          Where the barn is, what stallSIDE sends your clients, and what they can book
          themselves.
        </p>
      </header>

      {err && (
        <div className="rounded-lg bg-rose-50 text-rose-700 text-sm px-3 py-2">{err}</div>
      )}

      {!settingsQ.isLoading && (
        <div className="card p-4">
          <label className="block font-medium mb-1" htmlFor="timezone">
            Time zone
          </label>
          <p className="text-sm text-ink-500 mb-2">
            Every date in stallSIDE is worked out here: which day the board shows, when
            reminders go out, and whether a booking is past its cutoff.
          </p>
          <select
            id="timezone"
            className="input w-full sm:w-72"
            value={s?.timezone ?? 'America/New_York'}
            disabled={save.isPending}
            onChange={(e) => save.mutate({ timezone: e.target.value })}
          >
            {TIMEZONES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
            {/* A zone set elsewhere that isn't on the shortlist stays selectable
                rather than being silently swapped for Eastern on the next save. */}
            {s?.timezone && !TIMEZONES.some((t) => t.value === s.timezone) && (
              <option value={s.timezone}>{s.timezone}</option>
            )}
          </select>
          <p className="text-xs text-ink-400 mt-2">
            {localTimeIn(s?.timezone)
              ? `It's ${localTimeIn(s.timezone)} at the barn right now.`
              : ''}
          </p>
        </div>
      )}

      {settingsQ.isLoading ? (
        <div className="card p-4">
          <div className="h-4 w-40 bg-ink-100 rounded animate-pulse" />
        </div>
      ) : (
        <div className="card p-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="font-medium">Weekly schedule message</h2>
              <p className="text-sm text-ink-500 mt-1">
                Emails each client the lessons they have coming up in the next seven days.
                People with no email address on file are skipped.
              </p>
            </div>
            <Switch
              on={enabled}
              label="Weekly schedule message"
              disabled={save.isPending}
              onToggle={() => save.mutate({ weeklyScheduleEnabled: !enabled })}
            />
          </div>

          {enabled && (
            <div className="pt-3 border-t border-ink-100">
              <label className="block text-sm font-medium mb-1" htmlFor="send-day">
                Send day
              </label>
              <select
                id="send-day"
                className="input w-full sm:w-56"
                value={s?.weeklyScheduleDay ?? 0}
                disabled={save.isPending}
                onChange={(e) => save.mutate({ weeklyScheduleDay: Number(e.target.value) })}
              >
                {DAYS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-ink-400 mt-2">
                Sent overnight, in {s?.timezone || 'the barn'} time. Sunday means it arrives
                before the week it covers.
              </p>
            </div>
          )}
        </div>
      )}

      {!settingsQ.isLoading && (
        <div className="card p-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="font-medium">Lesson reminders</h2>
              <p className="text-sm text-ink-500 mt-1">
                Emails whoever is riding, and the people who own the horse, before a lesson.
              </p>
            </div>
            <Switch
              on={reminders}
              label="Lesson reminders"
              disabled={save.isPending}
              onToggle={() => save.mutate({ lessonRemindersEnabled: !reminders })}
            />
          </div>

          {reminders && (
            <div className="pt-3 border-t border-ink-100">
              <p className="text-sm font-medium mb-2">Send</p>
              <div className="flex flex-wrap gap-2">
                {LEADS.map((l) => {
                  const on = leads.includes(l.hours);
                  return (
                    <button
                      key={l.hours}
                      type="button"
                      aria-pressed={on}
                      disabled={save.isPending}
                      onClick={() => toggleLead(l.hours)}
                      className={
                        'text-sm px-3 py-1.5 rounded-full border transition-colors ' +
                        (on
                          ? 'bg-ink-800 text-white border-ink-800'
                          : 'bg-white text-ink-600 border-ink-200 hover:bg-ink-50')
                      }
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-ink-400 mt-2">
                Pick as many as you like. Each one sends its own email, so three ticked means
                three emails per lesson.
              </p>
            </div>
          )}
        </div>
      )}

      {!settingsQ.isLoading && (
        <div className="card p-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="font-medium">Change alerts</h2>
              <p className="text-sm text-ink-500 mt-1">
                Tells people straight away when a lesson is canceled or moved — by email, and
                by text when there's a mobile number on file. Cuts both ways: your clients hear
                when you cancel, and you hear when they do.
              </p>
            </div>
            <Switch
              on={alerts}
              label="Change alerts"
              disabled={save.isPending}
              onToggle={() => save.mutate({ changeAlertsEnabled: !alerts })}
            />
          </div>

          {alerts && (
            <div className="pt-3 border-t border-ink-100">
              <label className="block text-sm font-medium mb-1" htmlFor="alert-window">
                Only for lessons
              </label>
              <select
                id="alert-window"
                className="input w-full sm:w-56"
                value={s?.changeAlertWindowHours ?? 48}
                disabled={save.isPending}
                onChange={(e) => save.mutate({ changeAlertWindowHours: Number(e.target.value) })}
              >
                {WINDOWS.map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
              <p className="text-xs text-ink-400 mt-2">
                Moving a lesson three weeks out is ordinary scheduling. This only alerts on
                changes to lessons coming up soon.
              </p>
            </div>
          )}
        </div>
      )}

      {!settingsQ.isLoading && (
        <div className="card p-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="font-medium">Client booking</h2>
              <p className="text-sm text-ink-500 mt-1">
                Lets clients claim open slots from your published availability. They can
                always see the schedule — this decides whether they can book into it.
              </p>
            </div>
            <Switch
              on={booking}
              label="Client booking"
              disabled={save.isPending}
              onToggle={() => save.mutate({ clientBookingEnabled: !booking })}
            />
          </div>

          {booking && (
            <div className="pt-3 border-t border-ink-100">
              <label className="block text-sm font-medium mb-1" htmlFor="cutoff">
                Changes close
              </label>
              <select
                id="cutoff"
                className="input w-full sm:w-56"
                value={s?.bookingCutoffHours ?? 24}
                disabled={save.isPending}
                onChange={(e) => save.mutate({ bookingCutoffHours: Number(e.target.value) })}
              >
                {CUTOFFS.map((h) => (
                  <option key={h.value} value={h.value}>
                    {h.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-ink-400 mt-2">
                How close to a lesson a client may still book or cancel. You and your
                trainers are not held to this.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Switch({ on, label, disabled, onToggle }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={
        'shrink-0 w-11 h-6 rounded-full transition-colors relative ' +
        (on ? 'bg-emerald-600' : 'bg-ink-200')
      }
    >
      <span
        className={
          'absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ' +
          (on ? 'left-[22px]' : 'left-0.5')
        }
      />
    </button>
  );
}

function friendly(e) {
  const code = e?.body?.error || e?.message || '';
  if (code === 'invalid_day') return 'Pick a day of the week.';
  if (code === 'invalid_cutoff') return 'Pick a cutoff from the list.';
  if (code === 'invalid_reminder_hours') return 'Pick at least one time to send reminders.';
  if (code === 'invalid_window') return 'Pick a window from the list.';
  if (code === 'invalid_timezone') return 'That is not a time zone we recognize.';
  if (code === 'nothing_to_update') return 'Nothing changed.';
  if (code === 'forbidden') return 'Only barn owners and managers can change this.';
  return 'Could not save that. Try again.';
}

/**
 * The barn's wall-clock time right now.
 *
 * The point of showing it is that a wrong time zone is otherwise invisible —
 * you only find out when the board flips a day early or a reminder lands at
 * 3am. Seeing the barn's clock disagree with your watch says it immediately.
 */
function localTimeIn(timezone) {
  if (!timezone) return '';
  try {
    return new Date().toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    // An unknown zone throws RangeError. Say nothing rather than break the page.
    return '';
  }
}
